import Check from '@mui/icons-material/Check';
import { KeyboardArrowDown } from '@mui/icons-material';
import { Box, Button, MenuItem, Select, Typography } from '@mui/material';
import { TUserType } from '../../redux/user/actions';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useState } from 'react';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';

interface IRoleSelector {
    selectedRole: string;
    onRoleChange: (newRole: string) => void;
    variant: 'outlined' | 'standard' | 'filled';
    selectSx?: object;
    selectIconSx?: object;
    shouldUnderline?: boolean;
    confirmSx?: object;
    onRoleConfirm?: (selectedRole: string) => void;
}

const ROLES: TUserType[] = [
    'actor',
    'admin',
    'casting-director',
    'creator',
    'critic',
    'host',
    'producer',
    'regular',
    'super-admin',
];

export const RoleSelector: React.FC<IRoleSelector> = ({
    onRoleChange,
    selectedRole,
    variant,
    selectSx,
    selectIconSx,
    shouldUnderline,
    confirmSx,
    onRoleConfirm,
}) => {
    const { themeStyle } = useThemeContext();
    const [isEditMode, setIsEditMode] = useState(false);
    const [initialRole] = useState(selectedRole);
    const [newRole, setNewRole] = useState(selectedRole);

    const handleNewRoleSelection = (role: string) => {
        setNewRole(role);
        onRoleChange(role);
    };

    return (
        <Box display="flex" alignItems="center" gap={1}>
            <Select
                name="roleSelector"
                value={newRole}
                onClick={() => setIsEditMode(!isEditMode)}
                onChange={e => handleNewRoleSelection(e.target.value)}
                variant={variant}
                disableUnderline={!!shouldUnderline}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            backgroundColor: theme[themeStyle].tertiary_white,
                            color: theme[themeStyle].tag_white,
                            '& .MuiButtonBase-root:hover': {
                                backgroundColor: theme[themeStyle].role_hover,
                            },
                            '& .Mui-selected': {
                                backgroundColor: theme[themeStyle].selected_grey_orange,
                            },
                        },
                    },
                }}
                sx={{
                    fontSize: 12,
                    borderRadius: 0,
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    textTransform: 'capitalize',
                    height: 38,
                    width: 150,
                    borderBottomWidth: '1px',
                    '& .MuiSelect-icon': {
                        fontSize: 14,
                        ...selectIconSx,
                    },
                    ...selectSx,
                }}
                IconComponent={KeyboardArrowDown}
                renderValue={selected => selected as string}>
                {ROLES.map(role => {
                    return (
                        <MenuItem
                            key={role}
                            value={role}
                            sx={{
                                fontSize: 14,
                                width: '100%',
                                maxWidth: 275,
                                justifyContent: 'space-between',
                                position: 'relative',
                            }}>
                            <Typography
                                style={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 12, textTransform: 'capitalize' }}>
                                {role}
                            </Typography>
                            {selectedRole === role && (
                                <Check
                                    fontSize="small"
                                    sx={{
                                        position: 'absolute',
                                        right: 8,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                    }}
                                />
                            )}
                        </MenuItem>
                    );
                })}
            </Select>
            {isEditMode && initialRole !== newRole && (
                <Box sx={confirmSx} display="flex" alignItems="center" gap={0.5}>
                    <Button
                        onClick={() => {
                            onRoleConfirm && onRoleConfirm(newRole);
                            setIsEditMode(!isEditMode);
                        }}
                        color="success">
                        ✓
                    </Button>
                    <Button color="error" onClick={() => setIsEditMode(!isEditMode)}>
                        ✕
                    </Button>
                </Box>
            )}
        </Box>
    );
};
